import { BusinessDate } from '../../date/business-date';
import { normalizeName } from '../../shared/naming';
import { DEFAULT_FLOOR_NAME_PATTERN, floorNameForLevel } from '../../structure/floor';
import {
  deParaGroupKey,
  deParaNameKey,
  overrideKey,
  sourceKeyOf,
  STAGE_FLOORS,
  type DeParaOverride,
  type DeParaTarget,
  type GanttProDePara,
  type Placement,
  type StageFloor,
  type StageFloorKey,
} from './de-para';
import type { RowIssue } from './errors';
import type { ScheduleItemRowData } from './item-row';
import type { GanttProTask } from './read-tasks';
import { splitTaskName, type ParsedTaskName } from './task-name';

/**
 * Das tarefas do GanttPRO às linhas de item (Fase 25, RN-326, RN-327).
 *
 * Função pura: recebe as tarefas já lidas, o de-para e as escolhas do usuário, e devolve as
 * linhas **e** o que não soube resolver. Nada aqui grava; a conferência mostra o que sobrou
 * e o usuário escolhe — a escolha volta como `overrides` e a função roda de novo.
 *
 * ## O que uma tarefa vira
 *
 * Uma tarefa do GanttPRO pode virar **vários itens**, e é isso que dá trabalho:
 *
 * - **a barra de estrutura** ("Estrutura / 3º Pavimento - Tipo") vira um item de frente
 *   inteira em cada peça — PILARES, VIGAS, LAJE, ESCADAS — e cada serviço com a sua parte
 *   da janela, na ordem de execução e pelo HH (D1, RN-327, RN-329);
 * - **a tarefa sem pavimento** (a fachada por face, a porta corta-fogo, o elevador) vira um
 *   item **por pavimento**, marcado com quantidade provisória, e a face fica no trecho
 *   (D2, RN-326);
 * - **a tarefa de duas frentes** ("Bloco & Viga de Cintamento") vira dois itens;
 * - **duas tarefas que caem no mesmo item** viram um só quando o de-para manda juntar
 *   (`joinsSameItem`, D3): o corrimão da escada de incêndio entra no guarda-corpo, e o item
 *   leva a soma do HH, as predecessoras das duas e uma EDT só.
 *
 * O HH da tarefa é **repartido em partes iguais** entre os itens que ela gera, e a soma
 * confere com o arquivo: dividido em centésimos de hora, com o resto dos centésimos nas
 * primeiras partes. Tarefa que vira um item só leva o valor do arquivo, sem arredondar. Na
 * união, o item soma o HH das tarefas que o formam, e o total do arquivo segue igual.
 *
 * ## O nome do pavimento
 *
 * É o que a criação em sequência da estrutura dá: `{n}º Pavimento` (`floorNameForLevel`),
 * com o nível igual ao número — "14º Pavimento", nível 14. O que o cronograma chama de
 * "Tipo", "Barrilete" e "Barrilete Teto" é **tipo de pavimento**, e não faz parte do nome;
 * a tarefa de origem guarda o texto original em `sourceName`. Preliminar e Fundação são os
 * pavimentos de etapa da Fase 23, com nome e nível próprios (`STAGE_FLOORS`). Quem prefere
 * outro modelo de nome passa `floorNamePattern`.
 */

export interface ResolveOptions {
  /** A torre dona do cronograma — o arquivo do GanttPRO não diz, ele é de uma torre só. */
  readonly towerName: string;
  readonly dePara: GanttProDePara;
  /**
   * Escolhas do usuário na conferência, por `sourceKey`. **Só preenchem lacunas**: o de-para
   * manda no que ele conhece; a escolha vale para o nome que ele não conhece.
   */
  readonly overrides?: Readonly<Record<string, DeParaOverride>>;
  /** Modelo do nome do pavimento, com `{n}` no lugar do número. Padrão: `{n}º Pavimento`. */
  readonly floorNamePattern?: string;
  /** Os pavimentos de etapa. Padrão: `STAGE_FLOORS`. */
  readonly stageFloors?: Readonly<Record<StageFloorKey, StageFloor>>;
}

/** Um nome que o de-para não conhece: a conferência pergunta ao usuário. */
export interface UnresolvedName {
  /** A chave para `overrides`: "PINTURA / 1ª DEMÃO". */
  readonly sourceKey: string;
  /** O grupo da disciplina no cronograma. */
  readonly groupName: string;
  /** O grupo (ou o nome) do serviço no cronograma. */
  readonly serviceName: string;
  /** Quantas tarefas do arquivo têm este nome. */
  readonly count: number;
  /** O nome de uma delas, para o usuário reconhecer. */
  readonly sample: string;
}

/** O que o resolvedor usou e que ainda espera a aprovação do usuário (D3). */
export interface PendingApproval {
  readonly disciplineName: string;
  readonly serviceName: string | null;
  readonly newDiscipline: boolean;
  readonly note: string | undefined;
  /** Quantos itens dependem desta aprovação. */
  readonly items: number;
}

export interface GanttProResolution {
  readonly rows: ScheduleItemRowData[];
  readonly unresolved: UnresolvedName[];
  /** Tarefas que o de-para conhece mas que não puderam virar item (sem pavimento, por exemplo). */
  readonly issues: RowIssue[];
  readonly pendingApproval: PendingApproval[];
}

interface FloorRef {
  readonly name: string;
  readonly level: number;
}

interface Resolved {
  readonly task: GanttProTask;
  readonly parsed: ParsedTaskName;
  readonly targets: readonly DeParaTarget[] | undefined;
  readonly key: SourceKey;
}

interface SourceKey {
  readonly sourceKey: string;
  readonly groupName: string;
  readonly serviceName: string;
}

export function resolveGanttProTasks(
  tasks: readonly GanttProTask[],
  options: ResolveOptions,
): GanttProResolution {
  const floorPattern = options.floorNamePattern ?? DEFAULT_FLOOR_NAME_PATTERN;
  const stageFloors = options.stageFloors ?? STAGE_FLOORS;
  const floorAt = (level: number): FloorRef => ({
    name: floorNameForLevel(level, floorPattern),
    level,
  });

  const byName = new Map(
    options.dePara.byTaskName.map((entry) => [deParaNameKey(entry.taskName), entry.targets]),
  );
  const byGroup = new Map(
    options.dePara.byGroup.map((entry) => [
      deParaGroupKey(entry.group, entry.service),
      entry.targets,
    ]),
  );
  const overrides = new Map(
    Object.entries(options.overrides ?? {}).map(([key, value]) => [overrideKey(key), value]),
  );

  // ── 1. Cada tarefa, com os alvos que o de-para (ou a escolha do usuário) lhe dá ────────
  const resolved: Resolved[] = tasks
    .filter((task) => !task.isGroup)
    .map((task) => {
      const parsed = splitTaskName(task.name, task.groupPath);
      const key = keyOfTask(task, parsed);
      const grouped = groupNames(task);
      const targets =
        byName.get(deParaNameKey(task.name)) ??
        (grouped === undefined
          ? undefined
          : byGroup.get(deParaGroupKey(grouped.group, grouped.service))) ??
        overrideTargets(overrides.get(overrideKey(key.sourceKey)), parsed);
      return { task, parsed, targets, key };
    });

  // ── 2. Os pavimentos que o arquivo conhece ─────────────────────────────────────────────
  // A divisão da tarefa sem pavimento (D2) vai para os pavimentos que aparecem em tarefas
  // que **não** são de estrutura: o barrilete teto só tem a barra de estrutura, e não ganha
  // porta corta-fogo. A cobertura vai para o último pavimento do arquivo, de qualquer tarefa.
  const everyLevels = new Set<number>();
  const seenLevels = new Set<number>();
  for (const { parsed, targets } of resolved) {
    const level = floorLevelOf(parsed);
    if (level === undefined) continue;
    seenLevels.add(level);
    const isStructure = targets !== undefined && targets.every((t) => t.fromStructure === true);
    if (!isStructure) everyLevels.add(level);
  }
  const everyFloor = [...everyLevels].sort((a, b) => a - b).map(floorAt);
  const lastLevel = seenLevels.size === 0 ? undefined : Math.max(...seenLevels);

  // ── 3. As linhas ───────────────────────────────────────────────────────────────────────
  const built: BuiltRow[] = [];
  const issues: RowIssue[] = [];
  const unresolved = new Map<
    string,
    { -readonly [K in keyof UnresolvedName]: UnresolvedName[K] }
  >();

  for (const { task, parsed, targets, key } of resolved) {
    if (targets === undefined) {
      const found = unresolved.get(key.sourceKey);
      if (found === undefined) {
        unresolved.set(key.sourceKey, { ...key, count: 1, sample: task.name });
      } else {
        found.count += 1;
      }
      continue;
    }

    const pieces: { target: DeParaTarget; floor: FloorRef; segment: string | null }[] = [];
    for (const target of targets) {
      const where = floorsFor(target.placement, parsed, {
        everyFloor,
        lastLevel,
        floorAt,
        stageFloors,
      });
      if (typeof where === 'string') {
        issues.push({
          rowNumber: task.rowNumber,
          message: `"${task.name}" (EDT ${task.wbsCode}): ${where}`,
        });
        continue;
      }
      const segment = target.segment ?? segmentOf(parsed);
      for (const floor of where) pieces.push({ target, floor, segment });
    }

    const weighted = pieces.some(({ target }) => target.laborWeight !== undefined);
    const shares = weighted
      ? splitHoursByWeight(
          task.laborHours,
          pieces.map(({ target }) => target.laborWeight ?? 0),
        )
      : splitHours(task.laborHours, pieces.length);
    const windows = windowsOfPieces(task.start, task.end, pieces, shares);
    pieces.forEach(({ target, floor, segment }, index) => {
      built.push({
        target,
        row: {
          rowNumber: task.rowNumber,
          towerName: options.towerName,
          floorName: floor.name,
          floorLevel: floor.level,
          disciplineName: target.disciplineName,
          serviceName: target.serviceName,
          segment,
          wbsCode: task.wbsCode,
          sourceName: task.name,
          plannedStart: windows[index]?.start ?? task.start,
          plannedEnd: windows[index]?.end ?? task.end,
          predecessorWbsCodes: [...task.predecessorWbsCodes],
          quantity: null,
          unit: target.serviceName === null ? null : target.unit,
          // RN-326: a divisão pelos pavimentos é rateio, e a quantidade que vier dele é estimada.
          quantityProvisional: target.placement.kind === 'EVERY_FLOOR',
          laborHours: shares[index] ?? null,
        },
      });
    });
  }

  // ── 4. As tarefas que se juntam a outra (D3) ───────────────────────────────────────────
  const joined = joinRows(built);
  const rows = joined.map(({ row }) => row);

  // A aprovação conta os itens que **sobraram**: o que se uniu a outro já não depende dela.
  const pending = new Map<string, { target: DeParaTarget; items: number }>();
  for (const { target } of joined) {
    if (target.pendingApproval !== true) continue;
    const found = pending.get(pendingKey(target));
    if (found === undefined) pending.set(pendingKey(target), { target, items: 1 });
    else found.items += 1;
  }

  issues.push(...duplicateIssues(rows));

  return {
    rows,
    unresolved: [...unresolved.values()],
    issues,
    pendingApproval: [...pending.values()].map(({ target, items }) => ({
      disciplineName: target.disciplineName,
      serviceName: target.serviceName,
      newDiscipline: target.newDiscipline === true,
      note: target.note,
      items,
    })),
  };
}

// ── A união das tarefas que caem no mesmo item (D3) ─────────────────────────────────────

/** Uma linha de item e o alvo do de-para de que ela nasceu. */
interface BuiltRow {
  readonly row: ScheduleItemRowData;
  readonly target: DeParaTarget;
}

/** O alvo é `joinsSameItem`: a linha se junta à que já tem a mesma frente, serviço e trecho. */
const joins = (entry: BuiltRow): boolean => entry.target.joinsSameItem === true;

/** O limite de `sourceName` no contrato (`scheduleItemRowSchema`). */
const SOURCE_NAME_MAX = 300;

/**
 * Une as tarefas que o de-para manda juntar a outra (`joinsSameItem`).
 *
 * Para cada chave (andar, disciplina, serviço, trecho) a **base** é a primeira linha que não
 * se junta — ou, se todas se juntam, a primeira. As demais da chave que se juntam entram na
 * base: o HH e a quantidade somam, as predecessoras se juntam sem repetir, a janela se abre
 * para cobrir as duas, e a EDT e a linha de origem ficam as da base. Quem não tem a marca e
 * não é a base segue como está: é colisão de verdade, e o aviso a aponta (RN-320).
 *
 * Depois, a predecessora que apontava para uma EDT absorvida passa a apontar para a EDT da
 * base — a "Pintura - Esmalte sobre Ferro" esperava o corrimão, e agora espera o guarda-corpo.
 * Uma linha não é predecessora de si mesma (RN-323).
 */
function joinRows(built: readonly BuiltRow[]): BuiltRow[] {
  // A base de cada chave: a primeira que não se junta; se todas se juntam, a primeira.
  const hostOf = new Map<string, BuiltRow>();
  for (const entry of built) {
    const key = itemKey(entry.row);
    const current = hostOf.get(key);
    if (current === undefined || (joins(current) && !joins(entry))) hostOf.set(key, entry);
  }

  // As bases são copiadas antes de a primeira linha entrar nelas: o resolvedor não depende de
  // a tarefa que recebe vir antes da que se junta no arquivo.
  const copyOf = new Map<BuiltRow, BuiltRow>();
  for (const host of hostOf.values()) {
    copyOf.set(host, {
      ...host,
      row: { ...host.row, predecessorWbsCodes: [...host.row.predecessorWbsCodes] },
    });
  }

  /** A EDT absorvida → a EDT da base que a recebeu. */
  const absorbedInto = new Map<string, string>();
  const result: BuiltRow[] = [];
  for (const entry of built) {
    const host = hostOf.get(itemKey(entry.row));
    const hostCopy = host === undefined ? undefined : copyOf.get(host);
    if (host === undefined || hostCopy === undefined) continue;
    if (host === entry) {
      result.push(hostCopy);
    } else if (joins(entry)) {
      absorb(hostCopy.row, entry.row);
      if (entry.row.wbsCode !== null && hostCopy.row.wbsCode !== null) {
        absorbedInto.set(entry.row.wbsCode, hostCopy.row.wbsCode);
      }
    } else {
      // Colisão de verdade: a linha segue, e o aviso a aponta (RN-320).
      result.push(entry);
    }
  }
  if (absorbedInto.size === 0) return result;

  for (const { row } of result) {
    if (!row.predecessorWbsCodes.some((code) => absorbedInto.has(code))) continue;
    const rewritten: string[] = [];
    for (const code of row.predecessorWbsCodes) {
      const target = absorbedInto.get(code) ?? code;
      if (target === row.wbsCode || rewritten.includes(target)) continue;
      rewritten.push(target);
    }
    row.predecessorWbsCodes = rewritten;
  }
  return result;
}

/** A tarefa absorvida entra na linha-base: HH e quantidade somam, predecessoras e janela se juntam. */
function absorb(host: ScheduleItemRowData, absorbed: ScheduleItemRowData): void {
  host.laborHours = sumOrNull(host.laborHours, absorbed.laborHours);
  host.quantity = sumOrNull(host.quantity, absorbed.quantity);
  host.unit ??= absorbed.unit;
  host.quantityProvisional = host.quantityProvisional || absorbed.quantityProvisional;
  if (absorbed.plannedStart < host.plannedStart) host.plannedStart = absorbed.plannedStart;
  if (absorbed.plannedEnd > host.plannedEnd) host.plannedEnd = absorbed.plannedEnd;
  for (const code of absorbed.predecessorWbsCodes) {
    // RN-323: nem a EDT da base, nem a da absorvida, é predecessora do item que as une.
    if (code === host.wbsCode || code === absorbed.wbsCode) continue;
    if (!host.predecessorWbsCodes.includes(code)) host.predecessorWbsCodes.push(code);
  }
  if (absorbed.sourceName !== null && absorbed.sourceName !== host.sourceName) {
    const joinedName =
      host.sourceName === null
        ? absorbed.sourceName
        : `${host.sourceName} + ${absorbed.sourceName}`;
    host.sourceName =
      joinedName.length > SOURCE_NAME_MAX
        ? `${joinedName.slice(0, SOURCE_NAME_MAX - 1)}…`
        : joinedName;
  }
}

/** Soma em centésimos, para a soma ser exata; `null` só quando as duas partes são `null`. */
function sumOrNull(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.round(a * 100 + b * 100) / 100;
}

/** A chave do item: a mesma que o servidor usa para recusar o repetido (RN-320). */
function itemKey(row: ScheduleItemRowData): string {
  return [row.floorName, row.disciplineName, row.serviceName ?? '', row.segment ?? '']
    .map(normalizeName)
    .join('|');
}

// ── Onde cada alvo cai ──────────────────────────────────────────────────────────────────

interface Where {
  readonly everyFloor: readonly FloorRef[];
  readonly lastLevel: number | undefined;
  readonly floorAt: (level: number) => FloorRef;
  readonly stageFloors: Readonly<Record<StageFloorKey, StageFloor>>;
}

/** Os pavimentos do alvo, ou o motivo (texto) de não haver nenhum. */
function floorsFor(
  placement: Placement,
  parsed: ParsedTaskName,
  where: Where,
): FloorRef[] | string {
  switch (placement.kind) {
    case 'TASK_FLOOR': {
      const level = floorLevelOf(parsed, true);
      return level === undefined
        ? 'o nome da tarefa não diz o pavimento, e o de-para a espera num pavimento só.'
        : [where.floorAt(level)];
    }
    case 'EVERY_FLOOR':
      return where.everyFloor.length === 0
        ? 'não há pavimento no arquivo para dividir esta tarefa.'
        : [...where.everyFloor];
    case 'LAST_FLOOR':
      return where.lastLevel === undefined
        ? 'não há pavimento no arquivo para receber esta tarefa.'
        : [where.floorAt(where.lastLevel)];
    case 'FLOOR':
      return [where.floorAt(placement.level)];
    case 'STAGE': {
      const stage = where.stageFloors[placement.stage];
      return [{ name: stage.name, level: stage.level }];
    }
  }
}

/**
 * O número do pavimento que o nome da tarefa diz. Os muros ("Muros 1º Pavimento") só valem
 * quando a gente pede explicitamente: eles não são um pavimento, e não entram na lista dos
 * pavimentos do arquivo.
 */
function floorLevelOf(parsed: ParsedTaskName, includeWalls = false): number | undefined {
  const location = parsed.location;
  if (location.kind === 'FLOOR') return location.level;
  if (includeWalls && location.kind === 'WALLS') return location.level;
  return undefined;
}

/** O trecho que o nome da tarefa dá ao item: a face da fachada, ou os muros. */
function segmentOf(parsed: ParsedTaskName): string | null {
  const location = parsed.location;
  if (location.kind === 'FACADE') return location.face;
  if (location.kind === 'WALLS') return 'MUROS';
  return null;
}

// ── A chave de cada tarefa no de-para ────────────────────────────────────────────────────

/**
 * Os dois grupos acima da tarefa — a disciplina (nível 3 da EDT) e o serviço (nível 4), este
 * sem o prefixo da disciplina: o grupo "ESTRUTURA / CONVENCIONAL" sob "ESTRUTURA" é o
 * serviço "CONVENCIONAL". `undefined` quando a tarefa não tem dois grupos acima.
 */
function groupNames(task: GanttProTask): { group: string; service: string } | undefined {
  const path = task.groupPath;
  if (path.length < 2) return undefined;
  const group = path[path.length - 2] ?? '';
  const full = path[path.length - 1] ?? '';
  const prefix = `${normalizeName(group)} / `;
  const service = normalizeName(full).startsWith(prefix) ? full.slice(prefix.length) : full;
  return { group, service };
}

/**
 * A chave que a conferência mostra e o `overrides` devolve.
 *
 * É o par de grupos. No grupo "DIVERSOS" — onde o cliente joga o que não tem casa — o par
 * não separa nada (a porta corta-fogo e o corrimão têm o mesmo), então a chave leva também
 * o serviço escrito no nome.
 */
function keyOfTask(task: GanttProTask, parsed: ParsedTaskName): SourceKey {
  const grouped = groupNames(task);
  if (grouped === undefined) {
    return { sourceKey: parsed.raw, groupName: '', serviceName: parsed.service };
  }
  if (normalizeName(grouped.service) === 'diversos') {
    return {
      sourceKey: `${sourceKeyOf(grouped.group, grouped.service)} / ${parsed.service}`,
      groupName: grouped.group,
      serviceName: parsed.service,
    };
  }
  return {
    sourceKey: sourceKeyOf(grouped.group, grouped.service),
    groupName: grouped.group,
    serviceName: grouped.service,
  };
}

/**
 * O alvo que a escolha do usuário descreve. Sem o de-para para dizer onde, vale a regra da
 * fase: tarefa com pavimento no nome fica nele; tarefa sem pavimento se divide (RN-326).
 */
function overrideTargets(
  override: DeParaOverride | undefined,
  parsed: ParsedTaskName,
): DeParaTarget[] | undefined {
  if (override === undefined) return undefined;
  const hasFloor = parsed.location.kind === 'FLOOR' || parsed.location.kind === 'WALLS';
  return [
    {
      disciplineName: override.disciplineName,
      serviceName: override.serviceName,
      unit: null,
      placement: hasFloor ? { kind: 'TASK_FLOOR' } : { kind: 'EVERY_FLOOR' },
    },
  ];
}

// ── HH ───────────────────────────────────────────────────────────────────────────────────

/**
 * Reparte as horas-homem em `parts` partes iguais.
 *
 * Em centésimos de hora, para que a soma das partes seja **exatamente** o total: o que sobra
 * da divisão vai, um centésimo de cada vez, para as primeiras partes. Uma parte só leva o
 * valor como está.
 */
export function splitHours(total: number | null, parts: number): (number | null)[] {
  if (parts <= 0) return [];
  if (total === null) return Array.from({ length: parts }, () => null);
  if (parts === 1) return [total];

  const cents = Math.round(total * 100);
  const base = Math.floor(cents / parts);
  const remainder = cents - base * parts;
  return Array.from({ length: parts }, (_, index) => (base + (index < remainder ? 1 : 0)) / 100);
}

/**
 * Reparte as horas-homem pelos pesos (D5): a barra de estrutura vira fôrma, concretagem e
 * escoramento na proporção do HH próprio da CPU.
 *
 * Também em centésimos, e pelo maior resto: os centésimos que sobram do arredondamento vão
 * para as partes de maior fração, e a soma é **exatamente** o total. Peso 0 leva 0 — não
 * nulo, porque a tarefa tem HH e esse item, de empreiteiro, não gasta dele. Sem peso
 * positivo nenhum, a divisão volta a ser em partes iguais.
 */
export function splitHoursByWeight(
  total: number | null,
  weights: readonly number[],
): (number | null)[] {
  const parts = weights.length;
  if (parts === 0) return [];
  if (total === null) return Array.from({ length: parts }, () => null);
  const sum = weights.reduce((acc, weight) => acc + Math.max(0, weight), 0);
  if (sum <= 0) return splitHours(total, parts);

  const cents = Math.round(total * 100);
  const exact = weights.map((weight) => (cents * Math.max(0, weight)) / sum);
  const floors = exact.map((value) => Math.floor(value));
  let remainder = cents - floors.reduce((acc, value) => acc + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (const { index } of order) {
    if (remainder <= 0) break;
    floors[index] = (floors[index] ?? 0) + 1;
    remainder -= 1;
  }
  return floors.map((value) => value / 100);
}

// ── A janela de cada passo (RN-329) ──────────────────────────────────────────────────────

/** Uma janela de datas ISO, as duas pontas inclusive. */
export interface DateWindow {
  readonly start: string;
  readonly end: string;
}

/** As janelas das peças de uma barra, pavimento a pavimento: os passos não cruzam pavimentos. */
function windowsOfPieces(
  start: string,
  end: string,
  pieces: readonly { readonly target: DeParaTarget; readonly floor: FloorRef }[],
  shares: readonly (number | null)[],
): DateWindow[] {
  const result: DateWindow[] = pieces.map(() => ({ start, end }));
  if (!pieces.some(({ target }) => target.executionStep !== undefined)) return result;
  const byFloor = new Map<string, number[]>();
  pieces.forEach(({ floor }, index) => {
    const indexes = byFloor.get(floor.name) ?? [];
    indexes.push(index);
    byFloor.set(floor.name, indexes);
  });
  for (const indexes of byFloor.values()) {
    const windows = splitWindowBySteps(
      start,
      end,
      indexes.map((index) => ({
        step: pieces[index]?.target.executionStep,
        hours: shares[index] ?? null,
      })),
    );
    indexes.forEach((index, position) => {
      const window = windows[position];
      if (window !== undefined) result[index] = window;
    });
  }
  return result;
}

/**
 * Reparte a janela da barra pelos passos da ordem de execução (RN-329): a estrutura vira
 * confecção, escoramento, montagem, concreto e desmontagem, um depois do outro.
 *
 * - Os dias são os **úteis** da janela, de segunda a sexta, o calendário do GanttPRO.
 * - Cada passo leva dias na proporção do HH dele, a soma das partes que estão nele, em
 *   centésimos. As fronteiras são o arredondamento do acumulado: o primeiro passo começa no
 *   início da barra, o último termina no fim, e um não sobrepõe o outro.
 * - O passo que o arredondamento deixaria sem dia leva um dia só, o primeiro que lhe caberia.
 * - O passo sem HH (a armação, de empreiteiro) fica com a janela do passo com HH de antes; se
 *   não houver, com a do passo de depois.
 * - A parte sem passo, a barra sem HH, um passo só ou a janela sem dia útil: a janela inteira.
 */
export function splitWindowBySteps(
  start: string,
  end: string,
  parts: readonly { readonly step: number | undefined; readonly hours: number | null }[],
): DateWindow[] {
  const whole = parts.map(() => ({ start, end }));
  const first = BusinessDate.parse(start);
  const last = BusinessDate.parse(end);
  if (!first.ok || !last.ok || first.value.isAfter(last.value)) return whole;

  const days: string[] = [];
  for (let day = first.value; !day.isAfter(last.value); day = day.addDays(1)) {
    if (day.weekday() <= 5) days.push(day.value);
  }

  const cents = new Map<number, number>();
  for (const { step, hours } of parts) {
    if (step === undefined) continue;
    const value = Math.max(0, Math.round((hours ?? 0) * 100));
    cents.set(step, (cents.get(step) ?? 0) + value);
  }
  const steps = [...cents.keys()].sort((a, b) => a - b);
  const positive = steps.filter((step) => (cents.get(step) ?? 0) > 0);
  const total = positive.reduce((acc, step) => acc + (cents.get(step) ?? 0), 0);
  const count = days.length;
  if (count === 0 || positive.length < 2 || total <= 0) return whole;

  const windowOf = new Map<number, DateWindow>();
  let cumulative = 0;
  let from = 0;
  for (const step of positive) {
    cumulative += cents.get(step) ?? 0;
    // O acumulado arredondado, só com inteiros: floor(n × acumulado / total + ½).
    const to = Math.floor((2 * count * cumulative + total) / (2 * total));
    const startIndex = Math.min(from, count - 1);
    const endIndex = Math.max(startIndex, to - 1);
    windowOf.set(step, { start: days[startIndex] ?? start, end: days[endIndex] ?? end });
    from = Math.max(from, to);
  }
  for (const step of steps) {
    if (windowOf.has(step)) continue;
    const before = positive.filter((other) => other < step).at(-1);
    const after = positive.find((other) => other > step);
    const borrowed = windowOf.get(before ?? after ?? Number.NaN);
    if (borrowed !== undefined) windowOf.set(step, borrowed);
  }
  return parts.map(({ step }) =>
    step === undefined ? { start, end } : (windowOf.get(step) ?? { start, end }),
  );
}

// ── Conferências ─────────────────────────────────────────────────────────────────────────

/** RN-320: dois itens na mesma frente, serviço e trecho. As duas linhas seguem; o servidor as recusa. */
function duplicateIssues(rows: readonly ScheduleItemRowData[]): RowIssue[] {
  const seen = new Map<string, ScheduleItemRowData>();
  const issues: RowIssue[] = [];
  for (const row of rows) {
    const key = itemKey(row);
    const first = seen.get(key);
    if (first === undefined) {
      seen.set(key, row);
      continue;
    }
    issues.push({
      rowNumber: row.rowNumber,
      message:
        `A linha ${String(row.rowNumber)} cai na mesma frente, serviço e trecho da linha ` +
        `${String(first.rowNumber)}: ${row.floorName}, ${row.disciplineName}` +
        `${row.serviceName === null ? '' : ` / ${row.serviceName}`}` +
        `${row.segment === null ? '' : ` (${row.segment})`}.`,
    });
  }
  return issues;
}

function pendingKey(target: DeParaTarget): string {
  return `${normalizeName(target.disciplineName)}|${normalizeName(target.serviceName ?? '')}`;
}
